import { AdminMessage } from "@components/admin/admin-helpers"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { Button } from "@components/ui/Button"
import { formatDateTime } from "@utils/date-format"
import { defineBlock, EditableText } from "eddev/blocks"
import { Link } from "eddev/routing"
import { ReactNode } from "react"
import { ChildFilmTileFragment, MicroSessionTileFragment } from "@generated-types"
import { Chip } from "@components/ui/Chip"

export const meta: BlockMeta = {
  title: "Event Hierarchy",
  icon: "format-aside",
  tags: ["film-text"],
  description:
    "Displays information about child and parent events. This is usually the case for short films, features with a short film, or a collection of shorts.",
  category: "Film",
  conditions: {
    multiple: false,
  },
}

export default defineBlock("film/film-hierarchy", (props) => {
  let contents: ReactNode = null
  const parentEvents = props.film?.sessions?.flatMap((session) => session?.parentSession ?? []) ?? []
  const childFilms = props.film?.childFilms

  if (parentEvents.length > 0) {
    contents = (
      <>
        {contents}
        <div className="flex flex-col gap-5 my-10" data-mixpanel-area="Parent Films List">
          <EditableText
            store="parentsLabel"
            as="h4"
            className="type-label-caps text-grey"
            defaultValue={`'${props.film?.title}' is screening with`}
          />
          <div className="flex flex-col gap-4">
            {parentEvents.map((parentEvent) => (
              <MicroSessionTile key={parentEvent.id} {...parentEvent!} />
            ))}
          </div>
        </div>
      </>
    )
  }

  if (childFilms?.length) {
    contents = (
      <>
        {contents}
        <div className="flex flex-col gap-5 my-10" data-mixpanel-area="Child Films List">
          <EditableText
            store="childrenLabel"
            as="h4"
            className="type-label-caps text-grey"
            defaultValue="Films showing at this event"
          />
          <div className="flex flex-col gap-4">
            {childFilms.map((film) => (
              <MicroFilmTile key={film?.uri} {...film!} />
            ))}
          </div>
        </div>
      </>
    )
  }

  if (contents) {
    return <>{contents}</>
  } else if (env.admin) {
    return <AdminMessage>No parent or child events to display.</AdminMessage>
  }
})

function MicroSessionTile(props: MicroSessionTileFragment) {
  return (
    <Link
      href={props.uri}
      className="flex items-center gap-5 rounded-film md:hover:bg-neutral-100 transition-colors pr-4 group"
      title={props.film?.title!}
    >
      <ResponsiveImage {...props?.film?.media?.thumb} className="w-microFilmTileImageW aspect-video flex-none" />
      <div className="stack-y-2 flex-1">
        <h4 className="type-label-m">{props.film?.title}</h4>
        <div className="flex gap-2 flex-col md:flex-row md:items-center type-body-s">
          <p>{formatDateTime(props.info?.dateTime!)}</p>
          <Chip variant="mid" shape="rounded" className="line-clamp-1 md:line-clamp-none md:group-hover:bg-neutral-200">
            {props.info?.venue?.title}
          </Chip>
        </div>
      </div>
    </Link>
  )
}

function MicroFilmTile(props: ChildFilmTileFragment) {
  return (
    <Link
      href={props.uri}
      className="flex items-center gap-5 rounded-film md:hover:bg-neutral-100 transition-colors pr-4"
    >
      <div className="w-microFilmTileImageW aspect-video relative">
        <ResponsiveImage {...props?.media?.thumb} className="w-full h-full flex-none" />
        <div className="absolute w-full p-2 left-0 top-0 flex gap-2">{/* <Chip variant="hero">Short</Chip> */}</div>
      </div>
      <div className="stack-y-2 flex-1">
        <h4 className="type-label-m">{props?.title}</h4>
        <p
          className="type-body-s text-grey line-clamp-2 md:line-clamp-none"
          dangerouslySetInnerHTML={{ __html: props.excerpt! }}
        />
      </div>
    </Link>
  )
}
